import { Button, Typography } from "@mui/material";
import { makeStyles } from "@mui/styles";
import { useGridApiRef } from "@mui/x-data-grid-pro";
import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchEmployeeTimeOffRequestSnapshots,
  fetchTimeOffRequestSnapshotByReferenceNumber,
} from "../api/timeOffRequest";
import { isUserAuthorizedByServer } from "../api/userAccess";
import EmployeeTimeOffRequestTable from "../components/EmployeeTimeOffRequestTable";
import PageLoading from "../components/PageLoading";
import TimeOffFilterFormDesktop from "../components/TimeOffFilterFormDesktop";
import TimeOffFilterFormMobile from "../components/TimeOffFilterFormMobile";
import StarsBreadcrumbs from "../components/fundamentals/StarsBreadcrumbs";
import {
  selectTimeOffRequest,
  updateDateTypeFilter,
  updateEndDateFilter,
  updateEndDateFilterErrors,
  updateMobileSortByValue,
  updateSearchString,
  updateSearchStringErrors,
  updateStartDateFilter,
  updateStartDateFilterErrors,
  updateTimeOffRequestFoundByReferenceNumberSearch,
  updateTimeOffRequestFoundByReferenceNumberSearchIsDialogOpen,
  updateTimeOffRequestFoundByReferenceNumberSearchIsLoading,
  updateTimeOffStatusFilters,
  updateTimeOffTypeFilters,
} from "../slices/timeOffRequest/timeOffRequestSlice";
import { selectUserSession } from "../slices/userSession/userSessionSlice";
import { selectWebSocketEventData } from "../slices/webSocketEvent/webSocketEventSlice";
import { logout } from "../util/authentication";
import {
  ALL_TIME_OFF_STATUSES,
  ALL_TIME_OFF_TYPES,
  BREADCRUMBS_TITLE,
  ERROR_MAX_PAYLOAD_LIMIT_MSG,
  FEATURES,
  HOME_PATH,
} from "../util/constants";
import { deserializeDateFromRedux, serializeDateToRedux, set_time_to_end_of_day } from "../util/dateTimeFormatter";
import { timeOffRequestDataErrorCodeToErrorMessageMap } from "../util/errorMapper";
import {
  DEFAULT_EMPLOYEE_TIME_OFF_END_DATE_FILTER,
  DEFAULT_EMPLOYEE_TIME_OFF_START_DATE_FILTER,
  filterTimeOffRequests,
} from "../util/filterTimeOffRequests";
import { REQUEST_TIME_OFF_TITLE } from "../util/messageConstants";
import { initializeSearchIndex, searchTimeOffRequests } from "../util/searchTimeOffRequests";
import { sortEmployeeTimeOffRequestsByFilter } from "../util/sortTimeOffRequests";
import {
  validateEmployeeTorEndDateFilter,
  validateEmployeeTorStartDateFilter,
} from "../util/timeOffRequestInputValidation";
import { getScrubbedTimeOffRequestSnapshot, getScrubbedTimeOffRequestSnapshots } from "../util/timeOffRequestScrubber";
import { mergeWebSocketEmpTORs } from "../util/webSocketUtils";

const useStyles = makeStyles((theme) => ({
  requestTimeOffButtonDesktop: {
    height: "40px",
    fontSize: "0.8rem",
    float: "right",
    marginRight: ".9vw",
    marginBottom: "10px",
  },
  requestTimeOffButtonMobile: {
    width: "95.45vw",
  },
  buttonText: {
    color: theme.palette.secondary.swaWhite,
  },
}));

const MyTimeOffPage = (props) => {
  const classes = useStyles();
  const userSession = useSelector(selectUserSession);
  const employeeTimeOffRequestData = useSelector(selectTimeOffRequest);
  const webSocketEventData = useSelector(selectWebSocketEventData);

  const _placeHolderFunc = () => {
    "";
  }; // Place holder to pass to props

  const employeeTimeOffPageTitle = "My \nTime Off";
  const dispatch = useDispatch();
  const [isFirstLoad, setIsFirstLoad] = useState(true);
  const [isLoading, setIsLoading] = useState(true);
  const [isError, setIsError] = useState(false);
  const [errorObject, setErrorObject] = useState({});
  const [employeeTimeOffRequests, setEmployeeTimeOffRequests] = useState([]);
  const [filteredEmployeeTimeOffRequests, setFilteredEmployeeTimeOffRequests] = useState([]);
  const [searchedEmployeeTimeOffRequests, setSearchedEmployeeTimeOffRequests] = useState([]);
  const [isSearch, setIsSearch] = useState(false);
  const [openMobileFilterDialog, setOpenMobileFilterDialog] = useState(false);
  const mobileOriginalTimeOffTypeFiltersRef = useRef(ALL_TIME_OFF_TYPES);
  const mobileOriginalStatusFiltersRef = useRef(ALL_TIME_OFF_STATUSES);

  const dateTypeFilterRef = useRef("");
  const startDateFilterRef = useRef(null);
  const endDateFilterRef = useRef(null);
  const tempWebSocketDataRef = useRef({});
  const torTableDesktopRef = useGridApiRef();
  const torTableMobileRef = useGridApiRef();
  const isAdminPageRef = useRef(false);
  const isApiSubscribedRef = useRef(true);
  const [isUserAuthorizedState, setIsUserAuthorizedState] = useState(false);
  const [isUserAuthorizedStateLoading, setIsUserAuthorizedStateLoading] = useState(true);

  useEffect(() => {
    if (!isUserAuthorizedState) {
      isUserAuthorized();
    } else {
      if (employeeTimeOffRequestData.startDateFilter === null) {
        initializeFilterState();
      }
      if (
        employeeTimeOffRequestData?.startDateFilterErrors === "" &&
        employeeTimeOffRequestData?.endDateFilterErrors === ""
      ) {
        updateEmployeeTimeOffRequestSnapshotList(
          userSession.eid,
          userSession.activeStation,
          employeeTimeOffRequestData.dateTypeFilter,
          getStartDateFilterSafely(),
          getEndDateFilterSafely()
        );
      } else {
        setIsLoading(false);
      }
    }
    return () => {
      // Cleaning up lingering Promise handling...
      isApiSubscribedRef.current = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isUserAuthorizedState]);

  useEffect(() => {
    tempWebSocketDataRef.current = webSocketEventData.torEmpEvents;
    if (Object.values(webSocketEventData.torEmpEvents).length > 0) {
      if (!isFirstLoad) {
        setEmployeeTimeOffRequests((prevState) => {
          return mergeWebSocketEmpTORs(webSocketEventData.torEmpEvents, prevState);
        });
        tempWebSocketDataRef.current = {};
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [webSocketEventData.torEmpEvents]);

  useEffect(() => {
    let newFilteredTimeOffRequests = filterTimeOffRequests(
      employeeTimeOffRequests,
      employeeTimeOffRequestData.dateTypeFilter,
      getStartDateFilterSafely(),
      getEndDateFilterSafely(),
      employeeTimeOffRequestData.timeOffTypeFilters,
      employeeTimeOffRequestData.timeOffStatusFilters,
      null,
      isAdminPageRef.current
    );
    newFilteredTimeOffRequests = sortEmployeeTimeOffRequestsByFilter(
      employeeTimeOffRequestData.mobileSortByValue,
      newFilteredTimeOffRequests
    );
    applyFilters(newFilteredTimeOffRequests);
    if (employeeTimeOffRequestData.searchString !== "") {
      handleSearchSubmit(employeeTimeOffRequestData.searchString);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [employeeTimeOffRequests]);

  const isUserAuthorized = () => {
    isUserAuthorizedByServer(userSession).then((is200) => {
      if (is200) {
        setIsUserAuthorizedState(true);
      } else {
        logout();
      }
      setIsUserAuthorizedStateLoading(false);
    });
  };

  const doesResponseQueryParamsMatchCurrentFilters = (responseQueryParams) => {
    return (
      responseQueryParams.eid === userSession.eid &&
      decodeURI(responseQueryParams.dateType) === dateTypeFilterRef.current &&
      responseQueryParams.startDate === startDateFilterRef.current.toMillis().toString() &&
      responseQueryParams.endDate === endDateFilterRef.current.toMillis().toString()
    );
  };

  const updateEmployeeTimeOffRequestSnapshotList = (eid, station, dateType, startDate, endDate) => {
    isApiSubscribedRef.current = true;
    setLoading(true);
    setIsError(false);
    setErrorObject({});
    dateTypeFilterRef.current = dateType;
    startDateFilterRef.current = startDate;
    endDateFilterRef.current = endDate;
    fetchEmployeeTimeOffRequestSnapshots(eid, dateType, startDate, endDate)
      .then((res) => res.json())
      .then((result) => {
        if (isApiSubscribedRef.current && doesResponseQueryParamsMatchCurrentFilters(result.queryParams)) {
          if (result.message) {
            if (result.message === ERROR_MAX_PAYLOAD_LIMIT_MSG) {
              setIsError(true);
              setErrorObject(timeOffRequestDataErrorCodeToErrorMessageMap.timeOffRequestDataMaxPayloadError);
            } else {
              throw new Error("Unexpected error");
            }
          } else {
            const scrubbedTimeOffRequestSnapshots = getScrubbedTimeOffRequestSnapshots(result.timeOffRequestSnapshots);
            setEmployeeTimeOffRequests(
              mergeWebSocketEmpTORs(tempWebSocketDataRef.current, scrubbedTimeOffRequestSnapshots)
            );
            tempWebSocketDataRef.current = {};
            setIsLoading(false);
            setIsFirstLoad(false);
          }
        }
      })
      .catch((err) => {
        console.error("error happened!", err);
        setIsLoading(false);
        setIsError(true);
        setErrorObject(timeOffRequestDataErrorCodeToErrorMessageMap.unexpectedTimeOffRequestDataError);
      });
  };

  const setLoading = (isLoading) => {
    if (!isFirstLoad) {
      setIsLoading(isLoading);
    }
  };

  const applySearch = (searchString, searchedTimeOffRequests) => {
    if (searchString === "") {
      setIsSearch(false);
      setSearchedEmployeeTimeOffRequests(searchedTimeOffRequests);
      applyTimeOffTableScroll();
      setLoading(false);
    } else {
      setIsSearch(true);
      setSearchedEmployeeTimeOffRequests(searchedTimeOffRequests);
      applyTimeOffTableScroll();
      setLoading(false);
    }
  };

  const applyFilters = (filteredTimeOffRequests) => {
    initializeSearchIndex(filteredTimeOffRequests, isAdminPageRef.current);

    if (isSearch) {
      searchTimeOffRequests(employeeTimeOffRequestData.searchString, applySearch, isAdminPageRef.current);
    }

    setFilteredEmployeeTimeOffRequests(filteredTimeOffRequests);
  };

  const initializeFilterState = () => {
    dispatch(
      updateStartDateFilter(
        serializeDateToRedux(DEFAULT_EMPLOYEE_TIME_OFF_START_DATE_FILTER(userSession.activeStation))
      )
    );
    dispatch(
      updateEndDateFilter(serializeDateToRedux(DEFAULT_EMPLOYEE_TIME_OFF_END_DATE_FILTER(userSession.activeStation)))
    );
  };

  const applyTimeOffTableScroll = () => {
    if (props.isDesktopView) {
      // This ref scrolls desktop trades to top
      const currentDesktop = torTableDesktopRef.current;
      if (currentDesktop !== null && currentDesktop.getRowModels && currentDesktop.getRowModels().size > 0) {
        currentDesktop.scrollToIndexes({ rowIndex: 0, colIndex: 0 });
      }
    } else {
      // This ref scrolls mobile trades to top
      const currentMobileScreen = torTableMobileRef.current;
      if (currentMobileScreen !== null && currentMobileScreen.children && currentMobileScreen.children.length > 0) {
        currentMobileScreen.children[0].parentNode.scrollTop = 0;
      }
    }
  };

  const getStartDateFilterSafely = () => {
    return employeeTimeOffRequestData.startDateFilter
      ? deserializeDateFromRedux(employeeTimeOffRequestData.startDateFilter)
      : DEFAULT_EMPLOYEE_TIME_OFF_START_DATE_FILTER(userSession.activeStation);
  };

  const getEndDateFilterSafely = () => {
    return employeeTimeOffRequestData.endDateFilter
      ? deserializeDateFromRedux(employeeTimeOffRequestData.endDateFilter)
      : DEFAULT_EMPLOYEE_TIME_OFF_END_DATE_FILTER(userSession.activeStation);
  };

  const isStatusSelectAllChecked = () => {
    return employeeTimeOffRequestData.timeOffStatusFilters.length === ALL_TIME_OFF_STATUSES.length;
  };

  const isStatusSelectAllIndeterminate = () => {
    return (
      employeeTimeOffRequestData.timeOffStatusFilters.length !== ALL_TIME_OFF_STATUSES.length &&
      employeeTimeOffRequestData.timeOffStatusFilters.length !== 0
    );
  };

  const isTimeOffTypeSelectAllChecked = () => {
    return employeeTimeOffRequestData.timeOffTypeFilters.length === ALL_TIME_OFF_TYPES.length;
  };

  const isTimeOffTypeSelectAllIndeterminate = () => {
    return (
      employeeTimeOffRequestData.timeOffTypeFilters.length !== ALL_TIME_OFF_TYPES.length &&
      employeeTimeOffRequestData.timeOffTypeFilters.length !== 0
    );
  };

  const handleSearchChange = (event) => {
    dispatch(updateSearchString(event.target.value));
  };

  const handleSearchBlur = (event) => {
    if (event.target.value.trim() === "") {
      handleSearchSubmit("");
    }
  };

  const handleSearchEnterKeyPressed = (event) => {
    if (event.key === "Enter") {
      handleSearchSubmit(employeeTimeOffRequestData.searchString);
    }
  };

  const handleSearchCancel = () => {
    dispatch(updateSearchString(""));
    handleSearchSubmit("");
  };

  const handleClickTimeOffRequestFoundByReferenceNumberSearchDialogOpen = () => {
    dispatch(updateTimeOffRequestFoundByReferenceNumberSearchIsDialogOpen(true));
  };

  const handleClickTimeOffRequestFoundByReferenceNumberSearchDialogClose = () => {
    dispatch(updateTimeOffRequestFoundByReferenceNumberSearchIsDialogOpen(false));
    dispatch(updateTimeOffRequestFoundByReferenceNumberSearch(null));
  };

  const handleSearchSubmit = (newSearchString) => {
    dispatch(updateSearchStringErrors(""));
    const newSearchStringTrimmed = newSearchString.trim();
    if (
      !isNaN(parseInt(newSearchStringTrimmed)) &&
      parseInt(newSearchStringTrimmed) >= 10000000 &&
      parseInt(newSearchStringTrimmed) <= 99999999
    ) {
      dispatch(updateTimeOffRequestFoundByReferenceNumberSearchIsLoading(true));
      fetchTimeOffRequestSnapshotByReferenceNumber(
        newSearchStringTrimmed,
        userSession.eid,
        userSession.activeStation,
        isAdminPageRef.current
      )
        .then((result) => {
          if (result.message) {
            dispatch(updateSearchStringErrors(result.message));
            dispatch(updateTimeOffRequestFoundByReferenceNumberSearchIsLoading(false));
          } else {
            let timeOffRequestSnapshot = result.torSnapshotObject;
            let scrubbedTimeOffRequestSnapshot = getScrubbedTimeOffRequestSnapshot(timeOffRequestSnapshot);
            dispatch(updateTimeOffRequestFoundByReferenceNumberSearch(timeOffRequestSnapshot));
            dispatch(updateTimeOffRequestFoundByReferenceNumberSearchIsLoading(false));
            applySearch(newSearchStringTrimmed, [scrubbedTimeOffRequestSnapshot]);
            dispatch(updateTimeOffRequestFoundByReferenceNumberSearchIsDialogOpen(true));
          }
        })
        .catch((err) => {
          console.error("error happened!", err);
          dispatch(updateTimeOffRequestFoundByReferenceNumberSearchIsDialogOpen(false));
          dispatch(updateTimeOffRequestFoundByReferenceNumberSearchIsLoading(false));
        });
    } else {
      searchTimeOffRequests(newSearchStringTrimmed, applySearch, isAdminPageRef.current);
    }
  };

  const handleStartDateFilterChange = (date) => {
    let newStartDateFilterErrors = validateEmployeeTorStartDateFilter(
      date,
      deserializeDateFromRedux(employeeTimeOffRequestData.endDateFilter),
      userSession.activeStation
    );
    dispatch(updateStartDateFilter(serializeDateToRedux(date)));
    dispatch(updateStartDateFilterErrors(newStartDateFilterErrors));
    if (newStartDateFilterErrors === "") {
      updateEmployeeTimeOffRequestSnapshotList(
        userSession.eid,
        userSession.activeStation,
        employeeTimeOffRequestData.dateTypeFilter,
        date,
        deserializeDateFromRedux(employeeTimeOffRequestData.endDateFilter)
      );

      dispatch(updateEndDateFilterErrors(""));
      applyTimeOffTableScroll();
    }
  };

  const handleEndDateFilterChange = (event) => {
    let date = set_time_to_end_of_day(event);
    let newEndDateFilterErrors = validateEmployeeTorEndDateFilter(
      date,
      deserializeDateFromRedux(employeeTimeOffRequestData.startDateFilter),
      userSession.activeStation
    );
    dispatch(updateEndDateFilter(serializeDateToRedux(date)));
    dispatch(updateEndDateFilterErrors(newEndDateFilterErrors));
    if (newEndDateFilterErrors === "") {
      updateEmployeeTimeOffRequestSnapshotList(
        userSession.eid,
        userSession.activeStation,
        employeeTimeOffRequestData.dateTypeFilter,
        deserializeDateFromRedux(employeeTimeOffRequestData.startDateFilter),
        date
      );

      dispatch(updateStartDateFilterErrors(""));
      applyTimeOffTableScroll();
    }
  };

  const handleDateTypeFilterChange = (event) => {
    updateEmployeeTimeOffRequestSnapshotList(
      userSession.eid,
      userSession.activeStation,
      event.target.value,
      deserializeDateFromRedux(employeeTimeOffRequestData.startDateFilter),
      deserializeDateFromRedux(employeeTimeOffRequestData.endDateFilter)
    );

    dispatch(updateDateTypeFilter(event.target.value));
    applyTimeOffTableScroll();
  };

  const handleStatusFilterChange = (event) => {
    let newStatusFilterSelectedList = event.target.value;
    if (newStatusFilterSelectedList.includes("Select All")) {
      if (isStatusSelectAllChecked()) {
        newStatusFilterSelectedList = [];
      } else {
        newStatusFilterSelectedList = ALL_TIME_OFF_STATUSES;
      }
    }

    setLoading(true);

    let newFilteredTimeOffRequests = filterTimeOffRequests(
      employeeTimeOffRequests,
      employeeTimeOffRequestData.dateTypeFilter,
      getStartDateFilterSafely(),
      getEndDateFilterSafely(),
      employeeTimeOffRequestData.timeOffTypeFilters,
      newStatusFilterSelectedList,
      null,
      isAdminPageRef.current
    );

    dispatch(updateTimeOffStatusFilters(newStatusFilterSelectedList));
    applyFilters(newFilteredTimeOffRequests);
    setLoading(false);
    applyTimeOffTableScroll();
  };

  const handleTimeOffTypeFilterChange = (event) => {
    let newTimeOffTypeFilterSelectedList = event.target.value;
    if (newTimeOffTypeFilterSelectedList.includes("Select All")) {
      if (isTimeOffTypeSelectAllChecked()) {
        newTimeOffTypeFilterSelectedList = [];
      } else {
        newTimeOffTypeFilterSelectedList = ALL_TIME_OFF_TYPES;
      }
    }
    setLoading(true);

    let newFilteredTimeOffRequests = filterTimeOffRequests(
      employeeTimeOffRequests,
      employeeTimeOffRequestData.dateTypeFilter,
      getStartDateFilterSafely(),
      getEndDateFilterSafely(),
      newTimeOffTypeFilterSelectedList,
      employeeTimeOffRequestData.timeOffStatusFilters,
      null,
      isAdminPageRef.current
    );

    dispatch(updateTimeOffTypeFilters(newTimeOffTypeFilterSelectedList));
    applyFilters(newFilteredTimeOffRequests);
    setLoading(false);
    applyTimeOffTableScroll();
  };

  const handleClickMobileFilterDialogOpen = () => {
    mobileOriginalStatusFiltersRef.current = employeeTimeOffRequestData.timeOffStatusFilters;
    mobileOriginalTimeOffTypeFiltersRef.current = employeeTimeOffRequestData.timeOffTypeFilters;
    setOpenMobileFilterDialog(true);
  };

  const handleClickMobileFilterDialogClose = () => {
    revertMobileFilters();
    setOpenMobileFilterDialog(false);
  };

  const handleClickMobileFilterDialogApplyFiltersButton = () => {
    if (
      employeeTimeOffRequestData.timeOffTypeFilters.length > 0 &&
      employeeTimeOffRequestData.timeOffStatusFilters.length > 0
    ) {
      if (
        mobileOriginalStatusFiltersRef.current === employeeTimeOffRequestData.timeOffStatusFilters &&
        mobileOriginalTimeOffTypeFiltersRef.current === employeeTimeOffRequestData.timeOffTypeFilters
      ) {
        setOpenMobileFilterDialog(false);
        return;
      }
      sortEmployeeTimeOffRequestsByFilter(
        employeeTimeOffRequestData.mobileSortByValue,
        filteredEmployeeTimeOffRequests
      );
      applyTimeOffTableScroll();
      setOpenMobileFilterDialog(false);
    }
  };

  const renderRequestTimeOffButton = () => {
    const tradeFormChildItems = props.featureConfig.childItems;
    const requestTimeOff = tradeFormChildItems.find((item) => item.key === FEATURES.REQUEST_TIME_OFF);
    if (!!requestTimeOff) {
      return (
        <Button
          className={props.isDesktopView ? classes.requestTimeOffButtonDesktop : classes.requestTimeOffButtonMobile}
          href={`#${requestTimeOff.path}`}
          color="primary"
          variant="contained"
        >
          <Typography variant="body2" className={classes.buttonText}>
            {REQUEST_TIME_OFF_TITLE.toUpperCase()}
          </Typography>
        </Button>
      );
    } else {
      return "";
    }
  };

  const revertMobileFilters = () => {
    setLoading(true);
    let newFilteredTimeOffRequests = filterTimeOffRequests(
      employeeTimeOffRequests,
      employeeTimeOffRequestData.dateTypeFilter,
      getStartDateFilterSafely(),
      getEndDateFilterSafely(),
      mobileOriginalTimeOffTypeFiltersRef.current,
      mobileOriginalStatusFiltersRef.current,
      null,
      isAdminPageRef.current
    );
    dispatch(updateTimeOffTypeFilters(mobileOriginalTimeOffTypeFiltersRef.current));
    dispatch(updateTimeOffStatusFilters(mobileOriginalStatusFiltersRef.current));
    applyFilters(newFilteredTimeOffRequests);
    setLoading(false);
  };
  const breadcrumbsList = [BREADCRUMBS_TITLE.HOME, BREADCRUMBS_TITLE.MY_TIME_OFF];
  const breadcrumbsLink = [HOME_PATH];
  const renderTimeOffFilterForm = () => {
    if (props.isDesktopView) {
      return (
        <>
          <StarsBreadcrumbs
            isConfirmationRequired={false}
            breadcrumbsLinks={breadcrumbsLink}
            breadcrumbsList={breadcrumbsList}
          />
          <TimeOffFilterFormDesktop
            title={employeeTimeOffPageTitle}
            onDateTypeFilterChange={handleDateTypeFilterChange}
            onStatusFilterChange={handleStatusFilterChange}
            isStatusSelectAllChecked={isStatusSelectAllChecked}
            isStatusSelectAllIndeterminate={isStatusSelectAllIndeterminate}
            startDateFilter={getStartDateFilterSafely()}
            onStartDateFilterChange={handleStartDateFilterChange}
            endDateFilter={getEndDateFilterSafely()}
            onEndDateFilterChange={handleEndDateFilterChange}
            onTimeOffTypeFilterChange={handleTimeOffTypeFilterChange}
            isTimeOffTypeSelectAllChecked={isTimeOffTypeSelectAllChecked}
            isTimeOffTypeSelectAllIndeterminate={isTimeOffTypeSelectAllIndeterminate}
            onSearchChange={handleSearchChange}
            onSearchBlur={handleSearchBlur}
            onSearchEnterKeyPressed={handleSearchEnterKeyPressed}
            onSearchCancel={handleSearchCancel}
            onSearchSubmit={handleSearchSubmit}
            isDesktopView={props.isDesktopView}
            onClickTimeOffRequestFoundByReferenceNumberSearchDialogOpen={
              handleClickTimeOffRequestFoundByReferenceNumberSearchDialogOpen
            }
            onClickTimeOffRequestFoundByReferenceNumberSearchDialogClose={
              handleClickTimeOffRequestFoundByReferenceNumberSearchDialogClose
            }
            dateTypeFilter={employeeTimeOffRequestData.dateTypeFilter}
            startDateFilterErrors={employeeTimeOffRequestData.startDateFilterErrors}
            endDateFilterErrors={employeeTimeOffRequestData.endDateFilterErrors}
            timeOffTypeFilters={employeeTimeOffRequestData.timeOffTypeFilters}
            timeOffStatusFilters={employeeTimeOffRequestData.timeOffStatusFilters}
            searchString={employeeTimeOffRequestData.searchString}
            searchStringErrors={employeeTimeOffRequestData.searchStringErrors}
            timeOffRequestFoundByReferenceNumberSearchIsLoading={
              employeeTimeOffRequestData.timeOffRequestFoundByReferenceNumberSearchIsLoading
            }
            timeOffRequestFoundByReferenceNumberSearch={
              employeeTimeOffRequestData.timeOffRequestFoundByReferenceNumberSearch
            }
            timeOffRequestFoundByReferenceNumberSearchIsDialogOpen={
              employeeTimeOffRequestData.timeOffRequestFoundByReferenceNumberSearchIsDialogOpen
            }
          />
        </>
      );
    } else {
      return (
        <TimeOffFilterFormMobile
          title={employeeTimeOffPageTitle}
          onSearchChange={handleSearchChange}
          onSearchBlur={handleSearchBlur}
          onSearchEnterKeyPressed={handleSearchEnterKeyPressed}
          onSearchCancel={handleSearchCancel}
          onSearchSubmit={handleSearchSubmit}
          onStatusFilterChange={handleStatusFilterChange}
          isDesktopView={props.isDesktopView}
          mobileSortByValue={employeeTimeOffRequestData.mobileSortByValue}
          onMobileSortByChange={handleMobileSortByChange}
          openMobileFilterDialog={openMobileFilterDialog}
          onClickMobileFilterDialogOpen={() => handleClickMobileFilterDialogOpen()}
          onMobileFilterDialogClose={handleClickMobileFilterDialogClose}
          onTimeOffTypeFilterChange={handleTimeOffTypeFilterChange}
          isTimeOffTypeSelectAllChecked={isTimeOffTypeSelectAllChecked}
          isTimeOffTypeSelectAllIndeterminate={isTimeOffTypeSelectAllIndeterminate}
          isStatusSelectAllChecked={isStatusSelectAllChecked}
          isStatusSelectAllIndeterminate={isStatusSelectAllIndeterminate}
          onClickMobileFilterDialogApplyFiltersButton={() => handleClickMobileFilterDialogApplyFiltersButton()}
          onClickTimeOffRequestFoundByReferenceNumberSearchDialogOpen={() =>
            handleClickTimeOffRequestFoundByReferenceNumberSearchDialogOpen()
          }
          onClickTimeOffRequestFoundByReferenceNumberSearchDialogClose={() =>
            handleClickTimeOffRequestFoundByReferenceNumberSearchDialogClose()
          }
        />
      );
    }
  };

  const handleMobileSortByChange = (event) => {
    sortMobileTimeOffRequests(event.target.value, filteredEmployeeTimeOffRequests);
    dispatch(updateMobileSortByValue(event.target.value));
    applyTimeOffTableScroll();
  };

  const sortMobileTimeOffRequests = (sortValue, employeeTimeOffRequests) => {
    const sortedTimeOffRequests = [...employeeTimeOffRequests];
    sortEmployeeTimeOffRequestsByFilter(sortValue, sortedTimeOffRequests);
    applyFilters(sortedTimeOffRequests);
    setFilteredEmployeeTimeOffRequests(sortedTimeOffRequests);
  };

  const renderTimeOffRequestTable = () => {
    return (
      <EmployeeTimeOffRequestTable
        isDesktopView={props.isDesktopView}
        timeOffRequests={isSearch ? searchedEmployeeTimeOffRequests : filteredEmployeeTimeOffRequests}
        isAdminPage={false}
        isFirstLoad={isFirstLoad}
        isLoading={isLoading}
        isError={isError}
        errorObject={errorObject}
        handleCancelClick={_placeHolderFunc}
        handleRetractClick={_placeHolderFunc}
        station={userSession.activeStation}
        torTableDesktopRef={torTableDesktopRef}
        torTableMobileRef={torTableMobileRef}
      />
    );
  };

  return (
    <div className="App-contents" data-testid="employee-time-offs-page">
      {isUserAuthorizedStateLoading ? (
        <PageLoading />
      ) : (
        <>
          {renderTimeOffFilterForm()}
          {renderRequestTimeOffButton()}
          {renderTimeOffRequestTable()}
        </>
      )}
    </div>
  );
};

export default MyTimeOffPage;
